/**
 * Recording Player
 *
 * Custom audio player for call recordings.
 */

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import { useEffect, useRef, useState } from 'react';

import type { RecordingInfo } from '../../domain/types';

interface RecordingPlayerProps {
  recording: RecordingInfo;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function RecordingPlayer({ recording }: RecordingPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      const nextDuration = Number.isFinite(audio.duration) ? audio.duration : 0;
      setDuration(nextDuration);
      setIsReady(true);
    };

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime || 0);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isSeeking]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setIsReady(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    audio.src = recording.url;
    audio.load();
  }, [recording.url]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    try {
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      console.warn('[RecordingPlayer] Failed to play recording', error);
      setIsPlaying(false);
    }
  };

  const handleSeekChange = (_: Event, value: number | number[]) => {
    const nextValue = Array.isArray(value) ? value[0] : value;
    setIsSeeking(true);
    setCurrentTime(nextValue);
  };

  const handleSeekCommit = (_: Event, value: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextValue = Array.isArray(value) ? value[0] : value;
    audio.currentTime = nextValue;
    setCurrentTime(nextValue);
    setIsSeeking(false);
  };

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        p: 2,
        bgcolor: 'action.hover',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Recording
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {recording.filename}
          </Typography>
        </Box>
        <Tooltip title="Download recording">
          <IconButton
            component="a"
            href={recording.url}
            target="_blank"
            rel="noreferrer"
            size="small"
            aria-label="Download recording"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <IconButton
          onClick={togglePlay}
          disabled={!isReady}
          color={isPlaying ? 'primary' : 'default'}
          aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        <Box sx={{ flex: 1 }}>
          <Slider
            value={duration > 0 ? Math.min(currentTime, duration) : 0}
            min={0}
            max={duration > 0 ? duration : 0}
            step={0.25}
            onChange={handleSeekChange}
            onChangeCommitted={handleSeekCommit}
            disabled={!isReady || duration <= 0}
            aria-label="Recording progress"
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72, textAlign: 'right' }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Typography>
      </Stack>

      <audio ref={audioRef} preload="metadata" />
    </Box>
  );
}
